import { useEffect, useRef } from 'react'

import { CURRI_VERSION, useAuth } from '../auth/AuthProvider.tsx'
import { useSandboxCredentials } from '../auth/useSandboxCredentials.ts'
import { loadScalar } from '../docs/scalar.ts'
import { scalarCustomCss } from '../theme/scalar-theme.ts'

// The Scalar REST reference, framed by the portal chrome.
//
// Scalar is mounted as a *widget* rather than being the page: it has no
// supported slot for hosting a custom screen (its `plugins` API exposes only a
// `content.end` view and expects a Vue component), so the portal owns the shell
// and hands Scalar the main pane. `createApiReference(element, config)` is the
// same public entry point Scalar's own HTML integration calls.
//
// The bundle is loaded as a script from `config.scalarCdnUrl` rather than
// imported through `@scalar/api-reference-react`, which would bundle megabytes
// of Scalar into the SPA for an API this file calls exactly once. The loader
// itself lives in docs/scalar.ts, shared with the Routing API reference.

export const Reference = () => {
  const { config, state } = useAuth()
  const mountPoint = useRef<HTMLDivElement>(null)

  // Scalar reads `authentication` once, at construction, so a credential
  // appearing or disappearing has to re-mount the reference.
  const authenticated = state.status === 'authenticated'
  const sandboxCredentials = useSandboxCredentials()

  useEffect(() => {
    const element = mountPoint.current
    if (!element) return

    let instance: { destroy?: () => void } | void
    let cancelled = false

    void loadScalar(config.scalarCdnUrl)
      .then(scalar => {
        if (cancelled || !mountPoint.current) return
        instance = scalar.createApiReference(mountPoint.current, {
          url: config.openApiUrl,
          customCss: scalarCustomCss,
          // Our chrome is the light-mode design; Scalar's own toggle would put
          // a second, conflicting theme control inside the pane.
          forceDarkModeState: 'light',
          hideDarkModeToggle: true,
          defaultHttpClient: { targetKey: 'shell', clientKey: 'curl' },
          // Would otherwise leave a working API key in localStorage.
          persistAuth: false,
          // Prefilled with the caller's *sandbox* key, so try-it works
          // immediately and cannot touch live data. Signed out, prefill Scalar's
          // OAuth form instead. Never both: Scalar sets `Authorization` from the
          // selected scheme, and a Bearer would silently override sandbox Basic.
          //
          // With no client provisioned, prefill nothing — matching what the bare
          // `/reference` route does (`surfaces/docs/index.ts`) and what the
          // service README documents. A prefilled empty `x-scalar-client-id`
          // renders a live Authorize button that sends the visitor to the
          // authorization server with `client_id=` and an error page.
          authentication: sandboxCredentials
            ? {
                preferredSecurityScheme: 'basicAuth',
                securitySchemes: { basicAuth: sandboxCredentials },
              }
            : config.clientId
              ? {
                  preferredSecurityScheme: 'oauth2',
                  securitySchemes: {
                    oauth2: {
                      flows: {
                        authorizationCode: {
                          'x-usePkce': 'SHA-256',
                          'x-scalar-client-id': config.clientId,
                          selectedScopes: config.scopes,
                        },
                      },
                    },
                  },
                }
              : undefined,
          oauth2RedirectUri: `${config.publicUrl}/`,
          // The version header is mandatory on /api, and a missing one turns
          // every try-it into a confusing 400. Only this header — see above.
          //
          // Scoped to origins this API actually answers on, not to the page
          // origin: try-it targets the OpenAPI document's `servers` entry, which
          // is the canonical public URL, so comparing against
          // `window.location.origin` would drop the header on a port-forward, a
          // preview container, or a staging alias — the same origins this shell
          // deliberately supports. Still a guard, because the header must not
          // leak to a third-party host someone points Scalar at.
          onRequestBuilt: ({ request }: { request: Request }) => {
            const allowed = new Set([
              window.location.origin,
              new URL(config.publicUrl).origin,
            ])
            if (!allowed.has(new URL(request.url).origin)) return
            if (!request.headers.has('Curri-Version')) {
              request.headers.set('Curri-Version', CURRI_VERSION)
            }
          },
        })
      })
      .catch((error: unknown) => {
        if (cancelled || !mountPoint.current) return
        mountPoint.current.textContent =
          error instanceof Error
            ? `The API reference could not be loaded: ${error.message}`
            : 'The API reference could not be loaded.'
      })

    return () => {
      cancelled = true
      instance?.destroy?.()
      // Scalar mounts into this node; clear it so a remount does not stack two
      // instances on top of each other.
      if (element) element.innerHTML = ''
    }
  }, [config, authenticated, sandboxCredentials])

  // Sizing lives in `.curri-scalar-mount` (theme/tokens.css) because it has to
  // reach Scalar's own wrapper element, which this component never renders.
  return (
    <div
      ref={mountPoint}
      className="curri-scalar-mount"
      data-testid="scalar-mount"
    />
  )
}
